import React, { useEffect, useState, useMemo } from "react";
import PageShell from "@/components/PageShell";
import {
  Smartphone,
  Apple,
  Download,
  ShieldCheck,
  Info,
  ArrowRight,
} from "lucide-react";
import { withAttribution } from "@/utils/attribution";

// -------------------------------------------------------------------------
// INSTALL URLs — LIVE
// iOS: official TestFlight beta invite
// Android: direct signed APK download (Emergent hosted)
// Values are read from REACT_APP_* env vars with production-safe fallbacks
// so a missing env var never breaks the /install experience for testers.
// -------------------------------------------------------------------------
const ANDROID_URL =
  process.env.REACT_APP_ANDROID_APK_URL ||
  "https://play.google.com/apps/internaltest/4701395835934262854";
const IOS_TESTFLIGHT_URL =
  process.env.REACT_APP_IOS_TESTFLIGHT_URL ||
  "https://testflight.apple.com/join/kY7uv1Vg";

// -------------------------------------------------------------------------
// Detect the visitor's platform for the auto-detect helper strip.
// -------------------------------------------------------------------------
function detectPlatform() {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || navigator.vendor || "";
  if (/android/i.test(ua)) return "android";
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return "ios";
  return "desktop";
}

const StoreButton = ({
  brand,
  headline,
  sub,
  href,
  disabled,
  icon: Icon,
  testId,
}) => {
  const commonStyle = {
    background: disabled
      ? "rgba(255,255,255,0.04)"
      : "linear-gradient(140deg, rgba(34,197,133,0.22) 0%, rgba(34,197,133,0.06) 100%)",
    border: disabled
      ? "1px dashed rgba(255,255,255,0.20)"
      : "1px solid rgba(34,197,133,0.45)",
    boxShadow: disabled
      ? "none"
      : "0 20px 40px -20px rgba(34,197,133,0.35), inset 0 1px 0 rgba(255,255,255,0.06)",
  };
  const Wrapper = disabled ? "div" : "a";
  const wrapperProps = disabled
    ? {}
    : { href, target: "_blank", rel: "noopener noreferrer" };

  return (
    <Wrapper
      {...wrapperProps}
      className={`group rounded-2xl p-5 md:p-6 flex items-center gap-4 transition-transform ${
        disabled ? "cursor-not-allowed" : "hover:-translate-y-[2px]"
      }`}
      style={commonStyle}
      data-testid={testId}
      aria-disabled={disabled}
    >
      <div
        className="shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center"
        style={{
          background: "rgba(4,17,31,0.6)",
          border: "1px solid rgba(255,255,255,0.10)",
        }}
      >
        <Icon size={28} className="text-[#22C585]" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#22C585] font-bold">
          {brand}
        </div>
        <div
          className="mt-1 text-xl md:text-[22px] font-extrabold text-white leading-tight"
          style={{ fontFamily: "Sora, Inter, sans-serif" }}
        >
          {headline}
        </div>
        <div className="mt-1 text-sm text-[#C7D0DF]">
          {disabled ? (
            <span className="inline-flex items-center gap-2 text-[#F8B70E]">
              <Info size={14} />
              Coming soon — link not yet published
            </span>
          ) : (
            sub
          )}
        </div>
      </div>
      {!disabled && (
        <div className="hidden md:flex text-white opacity-70 group-hover:opacity-100 transition-opacity">
          <ArrowRight size={20} />
        </div>
      )}
    </Wrapper>
  );
};

const Install = () => {
  const [platform, setPlatform] = useState("unknown");

  useEffect(() => {
    setPlatform(detectPlatform());
  }, []);

  const androidReady = Boolean(ANDROID_URL);
  const iosReady = Boolean(IOS_TESTFLIGHT_URL);
  // Append currently-stored ref/UTM params to the outbound download links so
  // TestFlight installs and APK downloads can be correlated back to the source.
  const iosHref = useMemo(
    () => (iosReady ? withAttribution(IOS_TESTFLIGHT_URL) : IOS_TESTFLIGHT_URL),
    [iosReady]
  );
  const androidHref = useMemo(
    () => (androidReady ? withAttribution(ANDROID_URL) : ANDROID_URL),
    [androidReady]
  );

  const recommendedText =
    platform === "ios"
      ? "We detected an iOS device — TestFlight is your recommended install."
      : platform === "android"
      ? "We detected an Android device — grab the Play Store / APK build below."
      : "Choose your platform below. On mobile devices, tap the matching store.";

  return (
    <PageShell navyTheme={true}>
      <section
        className="relative overflow-hidden"
        style={{
          background:
            "radial-gradient(1000px 500px at 50% -100px, rgba(34,197,133,0.18) 0%, rgba(34,197,133,0.00) 60%)",
        }}
      >
        <div className="mx-auto max-w-4xl px-4 md:px-6 py-14 md:py-20">
          <div className="text-center">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.22em]"
              style={{
                background: "rgba(34,197,133,0.12)",
                border: "1px solid rgba(34,197,133,0.45)",
                color: "#22C585",
              }}
            >
              <Download size={12} />
              Install SpendNova
            </div>
            <h1
              className="mt-6 text-4xl md:text-5xl font-extrabold text-white leading-tight tracking-tight"
              style={{ fontFamily: "Sora, Inter, sans-serif" }}
              data-testid="install-title"
            >
              Get SpendNova
              <span className="text-[#22C585]">™</span> on your device.
            </h1>
            <p className="mt-4 text-[#C7D0DF] max-w-2xl mx-auto">
              SpendNova is currently in <strong className="text-white">Private Beta</strong>.
              Choose your platform below — TestFlight for iOS / iPadOS and the
              Play Store (or signed APK) for Android.
            </p>

            {/* Auto-detect strip */}
            <div
              className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs"
              style={{
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.12)",
              }}
              data-testid="install-auto-detect"
            >
              <ShieldCheck size={13} className="text-[#22C585]" />
              <span className="text-[#C7D0DF]">{recommendedText}</span>
            </div>
          </div>

          {/* Store buttons */}
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            <StoreButton
              brand="iOS · iPadOS"
              headline="Join TestFlight"
              sub="Apple's official beta channel — updates arrive automatically."
              href={iosHref}
              disabled={!iosReady}
              icon={Apple}
              testId="install-ios-button"
            />
            <StoreButton
              brand="Android"
              headline="Get the APK / Play Store"
              sub="Signed by FetchDay LLC. Google Play beta channel & sideload build."
              href={androidHref}
              disabled={!androidReady}
              icon={Smartphone}
              testId="install-android-button"
            />
          </div>

          {/* Instructions */}
          <div
            className="mt-10 rounded-3xl p-6 md:p-8"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.10)",
            }}
            data-testid="install-instructions"
          >
            <h2
              className="text-lg md:text-xl font-bold text-white"
              style={{ fontFamily: "Sora, Inter, sans-serif" }}
            >
              What to expect after tapping install
            </h2>
            <ul className="mt-4 space-y-3 text-[#C7D0DF] text-sm md:text-[15px]">
              <li className="flex gap-2 items-start">
                <span className="text-[#22C585] mt-1">•</span>
                <span>
                  <strong className="text-white">iOS / iPadOS:</strong> tap
                  the TestFlight link, install Apple's TestFlight app if
                  prompted, then accept the SpendNova invite. Updates arrive
                  automatically inside TestFlight.
                </span>
              </li>
              <li className="flex gap-2 items-start">
                <span className="text-[#22C585] mt-1">•</span>
                <span>
                  <strong className="text-white">Android:</strong> tap the Play
                  Store / APK link, allow install from FetchDay LLC (if
                  sideloading), then open SpendNova and sign in.
                </span>
              </li>
              <li className="flex gap-2 items-start">
                <span className="text-[#22C585] mt-1">•</span>
                <span>
                  <strong className="text-white">Both platforms:</strong> Face
                  ID / biometric unlock is offered on first launch. All
                  financial data is encrypted in transit (TLS 1.3) and at
                  rest.
                </span>
              </li>
              <li className="flex gap-2 items-start">
                <span className="text-[#22C585] mt-1">•</span>
                <span>
                  Trouble installing? Email{" "}
                  <a
                    href="mailto:support@myspendnova.com"
                    className="text-[#22C585] hover:underline font-semibold"
                  >
                    support@myspendnova.com
                  </a>{" "}
                  — we usually reply within 1 business day.
                </span>
              </li>
            </ul>
          </div>

          {/* Privacy reassurance */}
          <div
            className="mt-6 rounded-2xl p-5 flex items-start gap-3"
            style={{
              background:
                "linear-gradient(120deg, rgba(34,197,133,0.14) 0%, rgba(34,197,133,0.04) 100%)",
              border: "1px solid rgba(34,197,133,0.35)",
            }}
          >
            <ShieldCheck size={20} className="text-[#22C585] shrink-0 mt-0.5" />
            <div className="text-sm text-white">
              <div className="font-semibold">Your money stays yours.</div>
              <p className="text-[#C7D0DF] mt-1 mb-0">
                We don't sell your data, train models on your finances, or
                share anything with advertisers — ever.
              </p>
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default Install;
